// Portfolio Administration v1.0 PR 4a §7.7 — vendor assignment drawer.
//
// "Assignment" today = a LoanPartyAssignment whose counterpartyType=Vendor.
// Termination here calls inactivate (same primitive the Parties tab uses).
// Vendor Billing v1.0 adds the per-assignment "Issue invoice" action
// (vendor prefilled, loan fixed to the current one).

import { useState } from "react";
import { Link } from "react-router-dom";
import { Button, StatusBadge, Timestamp, type StatusTone } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/api/client";
import {
  useConnectVendor,
  useDisconnectVendor,
  useInactivateLoanParty,
  useLoanVendorConnections,
} from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import { useLoanGoalsOverview } from "@/hooks/portfolioAdmin/useLoanGoals";
import { isOpenGoal } from "@/components/portfolioAdmin/loanDetail/goals/goalPresentation";
import { ResourceDrawer } from "../common/ResourceDrawer";
import { IssueInvoiceModal } from "@/components/portfolioAdmin/vendors/billing/IssueInvoiceModal";
import { useDateFormatter } from "@/utils/datetime";
import type {
  LoanPartyAssignmentDto,
  VendorDto,
} from "@/api/portfolioAdmin/types";

interface VendorAssignmentDrawerProps {
  loanId: string;
  /** Loan number for the issue-invoice modal's fixed-loan label. */
  loanNumber: string;
  assignment: LoanPartyAssignmentDto;
  vendor: VendorDto | undefined;
  open: boolean;
  onClose: () => void;
}

export function VendorAssignmentDrawer({
  loanId,
  loanNumber,
  assignment,
  vendor,
  open,
  onClose,
}: VendorAssignmentDrawerProps) {
  const toast = useToast();
  const auth = useAuth();
  // Mirrors the server gate on DELETE parties/{id} for a Vendor party:
  // Vendors.Manage OR Loans.Manage (same convergence as VendorsTab.canAssign).
  const canTerminate =
    auth.hasPermission("Vendors.Manage") || auth.hasPermission("Loans.Manage");
  const canIssueInvoice = auth.hasPermission("VendorBilling.Issue");
  const [issueOpen, setIssueOpen] = useState(false);

  const inactivate = useInactivateLoanParty(loanId);
  const { date } = useDateFormatter();

  const onTerminate = async () => {
    try {
      await inactivate.mutateAsync(assignment.id);
      toast.success("Vendor assignment terminated.");
      onClose();
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Could not terminate assignment.",
      );
    }
  };

  return (
    <>
    <ResourceDrawer
      open={open}
      onClose={onClose}
      title={vendor?.name ?? assignment.counterpartyId}
      subtitle={`${vendor?.vendorType ?? "Vendor"} · ${assignment.roleOnLoan}`}
      testId="vendor-assignment-drawer"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Close</Button>
          {canIssueInvoice && vendor && (
            <Button
              variant="primary"
              onClick={() => setIssueOpen(true)}
              data-testid="vendor-assignment-issue-invoice"
            >
              Issue invoice
            </Button>
          )}
          {canTerminate && assignment.isActive && (
            <Button
              variant="destructive"
              onClick={() => void onTerminate()}
              disabled={inactivate.isPending}
              data-testid="vendor-assignment-terminate"
            >
              Terminate assignment
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-space-4">
        <section>
          <h3 className="font-display font-semibold text-h4 m-0 mb-space-2">
            Assignment
          </h3>
          <dl className="grid grid-cols-2 gap-y-space-1 text-small m-0">
            <dt className="text-caption text-gray-mid uppercase tracking-label">Vendor</dt>
            <dd className="m-0">
              {vendor ? (
                <Link
                  to={`/portfolio-admin/vendors/${vendor.id}`}
                  className="text-gold-shadow hover:underline"
                >
                  {vendor.name}
                </Link>
              ) : (
                assignment.counterpartyId
              )}
            </dd>
            <dt className="text-caption text-gray-mid uppercase tracking-label">Type</dt>
            <dd className="m-0">{vendor?.vendorType ?? "—"}</dd>
            <dt className="text-caption text-gray-mid uppercase tracking-label">Role on loan</dt>
            <dd className="m-0">{assignment.roleOnLoan}</dd>
            <dt className="text-caption text-gray-mid uppercase tracking-label">Assigned</dt>
            <dd className="m-0">{date(assignment.createdAt)}</dd>
            {assignment.notes && (
              <>
                <dt className="text-caption text-gray-mid uppercase tracking-label">Notes</dt>
                <dd className="m-0 whitespace-pre-wrap">{assignment.notes}</dd>
              </>
            )}
          </dl>
        </section>

        <ConnectionSection loanId={loanId} assignment={assignment} />

        <AssignedGoalsSection loanId={loanId} assignmentId={assignment.id} />

        <section className="rounded-md bg-white-warm p-space-2">
          <p className="text-caption text-gray-mid m-0">
            VFI (Vendor Fee Income) invoices for this vendor live on the
            vendor record&apos;s <strong>VFI Ledger</strong> tab (link above).
            Agreement documents and performance scoring ship with the Vendor
            Program v3.0 module.
          </p>
        </section>
      </div>
    </ResourceDrawer>
    {issueOpen && vendor && (
      <IssueInvoiceModal
        open={issueOpen}
        onClose={() => setIssueOpen(false)}
        vendorId={vendor.id}
        vendorName={vendor.name}
        fixedLoan={{ loanId, label: loanNumber }}
      />
    )}
    </>
  );
}

const ENGAGEMENT_LABEL: Record<string, string> = {
  "new": "New",
  "contacted": "Contacted",
  "in-negotiation": "In negotiation",
  "under-contract": "Under contract",
};
const ENGAGEMENT_TONE: Record<string, StatusTone> = {
  "new": "info",
  "contacted": "progress",
  "in-negotiation": "warn",
  "under-contract": "ok",
};

/** Vendor Connection v1 — the explicit two-sided connection on this
 *  assignment. Make/Disconnect gate Vendors.Manage (server-enforced too);
 *  staff see both parties' notes + the engagement status here. */
function ConnectionSection({
  loanId,
  assignment,
}: {
  loanId: string;
  assignment: LoanPartyAssignmentDto;
}) {
  const toast = useToast();
  const auth = useAuth();
  const canManage = auth.hasPermission("Vendors.Manage");
  const connectionsQuery = useLoanVendorConnections(loanId);
  const connect = useConnectVendor(loanId);
  const disconnect = useDisconnectVendor(loanId);
  const [disconnectReason, setDisconnectReason] = useState("");
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  const live = (connectionsQuery.data ?? []).find(
    (c) => c.partyAssignmentId === assignment.id && c.isLive,
  );

  const onConnect = async () => {
    try {
      await connect.mutateAsync(assignment.id);
      toast.success("Connection made — both parties were notified.");
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Could not make the connection.",
      );
    }
  };

  const onDisconnect = async () => {
    if (!live) return;
    try {
      await disconnect.mutateAsync({
        connectionId: live.id,
        reason: disconnectReason.trim() || undefined,
      });
      setConfirmingDisconnect(false);
      setDisconnectReason("");
      toast.success("Connection disconnected.");
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Could not disconnect.",
      );
    }
  };

  return (
    <section data-testid="vendor-connection-section">
      <h3 className="font-display font-semibold text-h4 m-0 mb-space-2">
        Customer connection
      </h3>
      {connectionsQuery.isLoading && (
        <p className="text-small text-gray-mid italic m-0">Loading connection…</p>
      )}
      {connectionsQuery.isError && (
        <p className="text-small text-status-error m-0">
          Could not load the connection. Refresh to retry.
        </p>
      )}
      {connectionsQuery.data && !live && (
        <div className="flex flex-col gap-space-2">
          <p className="text-small text-gray-mid m-0">
            Assignment only — nothing is visible on either portal until you
            make the connection.
          </p>
          {canManage && assignment.isActive && (
            <div>
              <Button
                variant="primary"
                onClick={() => void onConnect()}
                disabled={connect.isPending}
                data-testid="vendor-connection-connect"
              >
                Make connection
              </Button>
            </div>
          )}
        </div>
      )}
      {live && (
        <div className="flex flex-col gap-space-2">
          <dl className="grid grid-cols-2 gap-y-space-1 text-small m-0">
            <dt className="text-caption text-gray-mid uppercase tracking-label">Connected</dt>
            <dd className="m-0">
              <Timestamp value={live.connectedAt} />
            </dd>
            <dt className="text-caption text-gray-mid uppercase tracking-label">Engagement</dt>
            <dd className="m-0">
              <StatusBadge tone={ENGAGEMENT_TONE[live.engagementStatus] ?? "neutral"}>
                {ENGAGEMENT_LABEL[live.engagementStatus] ?? live.engagementStatus}
              </StatusBadge>
            </dd>
            {live.vendorNotes && (
              <>
                <dt className="text-caption text-gray-mid uppercase tracking-label">
                  Vendor notes
                </dt>
                <dd className="m-0 whitespace-pre-wrap">{live.vendorNotes}</dd>
              </>
            )}
            {live.buyerNotes && (
              <>
                <dt className="text-caption text-gray-mid uppercase tracking-label">
                  Buyer notes
                </dt>
                <dd className="m-0 whitespace-pre-wrap">{live.buyerNotes}</dd>
              </>
            )}
          </dl>
          {canManage && !confirmingDisconnect && (
            <div>
              <Button
                variant="secondary"
                onClick={() => setConfirmingDisconnect(true)}
                data-testid="vendor-connection-disconnect"
              >
                Disconnect
              </Button>
            </div>
          )}
          {canManage && confirmingDisconnect && (
            <div className="flex flex-col gap-space-1">
              <label className="text-caption text-gray-mid" htmlFor="disconnect-reason">
                Reason (optional)
              </label>
              <input
                id="disconnect-reason"
                className="rounded-md border border-border p-space-1 text-small"
                value={disconnectReason}
                onChange={(e) => setDisconnectReason(e.target.value)}
                data-testid="vendor-connection-disconnect-reason"
              />
              <div className="flex gap-space-2">
                <Button
                  variant="destructive"
                  onClick={() => void onDisconnect()}
                  disabled={disconnect.isPending}
                  data-testid="vendor-connection-disconnect-confirm"
                >
                  Confirm disconnect
                </Button>
                <Button variant="secondary" onClick={() => setConfirmingDisconnect(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/** Read view of the loan-level goals carried by THIS assignment (Vendor
 *  Program v2 PR 1) — assignment happens on the loan's Goals tab. */
function AssignedGoalsSection({
  loanId,
  assignmentId,
}: {
  loanId: string;
  assignmentId: string;
}) {
  const goalsQuery = useLoanGoalsOverview(loanId);
  const assigned = (goalsQuery.data?.goals ?? []).filter(
    (g) => g.assignedPartyAssignmentId === assignmentId && isOpenGoal(g),
  );

  return (
    <section data-testid="vendor-assignment-goals">
      <h3 className="font-display font-semibold text-h4 m-0 mb-space-2">
        Assigned goals
      </h3>
      {goalsQuery.isLoading && (
        <p className="text-small text-gray-mid italic m-0">Loading goals…</p>
      )}
      {goalsQuery.isError && (
        <p className="text-small text-status-error m-0">
          Could not load goals. Refresh to retry.
        </p>
      )}
      {goalsQuery.data && assigned.length === 0 && (
        <p className="text-small text-gray-mid m-0">
          No goals assigned to this vendor —{" "}
          <Link
            to={`/portfolio-admin/loans/${loanId}?tab=goals`}
            className="text-gold-shadow hover:underline"
          >
            manage goals
          </Link>
          .
        </p>
      )}
      {assigned.length > 0 && (
        <ul className="m-0 p-0 list-none flex flex-col gap-space-1">
          {assigned.map((g) => (
            <li key={g.id} className="text-small">
              <Link
                to={`/portfolio-admin/loans/${loanId}?tab=goals&goal=${g.id}`}
                className="text-gold-shadow hover:underline"
              >
                {g.title}
              </Link>{" "}
              <span className="text-caption text-gray-mid">
                ({g.status === "active" ? "active" : "proposed"})
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
